<template>
  <div>
    <external-api-payment-type-toggler v-if="isExternalApi" />
    <div
      class="rounded-xl bg-light-blue-2 p-2 mx-0.5 md:p-2 md:mx-2"
      :class="{ 'card bg-root-1': useType !== 'externalApi' }"
    >
      <div class="pt-3 px-2 md:px-5 rounded-xl">
        <div class="flex justify-between mb-3">
          <p class="text-xs md:text-sm font-normal">You send exactly</p>
          <p class="text-xs md:text-base font-bold">
            {{ parseMoney(transferDetails.debitAmount) }}
          </p>
        </div>
        <div class="flex justify-between mb-3">
          <p class="text-xs md:text-sm font-normal">Our fees</p>
          <p class="text-13px font-normal">
            {{ parseMoneyAll(transferDetails.fees, country.currency) }}
          </p>
        </div>
        <div
          class="flex justify-between mb-3"
          v-if="!fromBalance && transferDetails.partnerCommission"
        >
          <p class="text-xs md:text-sm font-normal">My Commission</p>
          <p class="text-13px font-normal">
            {{
              parseMoneyAll(transferDetails.partnerCommission, country.currency)
            }}
          </p>
        </div>
        <div class="flex justify-between mb-3">
          <p class="text-xs md:text-sm font-normal">Amount converted</p>
          <p class="text-13px font-normal">
            {{
              parseMoneyAll(
                transferDetails.debitAmount - transferDetails.fees,
                country.currency
              )
            }}
          </p>
        </div>
        <div class="flex justify-between mb-3">
          <p class="text-xs md:text-sm font-normal">Exchange rate</p>
          <p class="text-13px font-normal">
            1
            {{ !this.isDivisionOperator ? activeCurrency : secondaryCurrency }}
            = {{ exchangeRateBase }}
            {{ !this.isDivisionOperator ? secondaryCurrency : activeCurrency }}
          </p>
        </div>
        <div class="flex justify-between pb-3">
          <p class="text-xs md:text-sm capitalize font-normal">
            {{ recipientInfo.recipientName || 'Recipient' }} gets
          </p>
          <p class="text-xs md:text-base font-bold">
            {{ convertValueTo2DP(transferDetails.recipientAmount || 0) }}
            {{ country2.currency }}
          </p>
        </div>
      </div>
    </div>
    <div>
      <div class="flex text-root-2 items-center justify-center mt-6">
        <hr class="flex-none w-1/4 border-gray-2" />
        <p class="text-root-2 font-semibold flex-none grow mx-3">
          Select Deposit Method
        </p>
        <hr class="border-gray-2 w-1/4" />
      </div>
      <div class="grid md:grid-cols-6 grid-cols-1 gap-5 mt-8 w-full">
        <div class="md:col-span-3 cursor-not-allowed opacity-40">
          <div class="border flex flex-col items-center justify-center box">
            <plaid-icon />
            <p class="text-xs md:text-sm font-semibold text-root-4 mt-6">
              Direct debit
            </p>
            <span class="text-root-4 text-xs font-normal"
              >(Powered by Plaid Ach)</span
            >
          </div>
        </div>
        <div
          class="md:col-span-3 cursor-pointer"
          @click="showWireTransferInstruction"
        >
          <div class="border flex flex-col items-center justify-center box">
            <div class="bg-orange-1 rounded-full p-3"><bank-icon-big /></div>
            <p class="text-xs md:text-sm font-semibold mt-6">Wire Transfer</p>
          </div>
        </div>
      </div>
    </div>
    <el-dialog
      :visible.sync="showWireTransferInstructionDialog"
      :width="modalWidth"
      :modal-append-to-body="false"
      :before-close="handleClose"
      top="10vh"
      custom-class="custom-scrollable"
    >
      <wire-transfer-details
        @closeDialog="done"
        :transferType="transferType"
        :useType="useType"
      />
    </el-dialog>
    <transaction-processing
      v-if="showTransferProcessingDialog"
      :showDialog="showTransferProcessingDialog"
      @closeDialog="closeTransferProcessingDialog"
      @showSuccess="showSuccess"
    />
    <beneficiary-saved-success-dialog
      v-if="showSuccessDialog"
      :showDialog="showSuccessDialog"
      @closeDialog="closeSuccessDialog"
    />
  </div>
</template>

<script>
import country from '@/mixins/getCountryDetails.js'
import exchangeRate from '@/mixins/localDeposit.js'
import breakpoints from '@/utils/breakpoints.js'

import {
  ConvertValueToXDP,
  ParseMoneyNaira,
  ParseMoney,
  ParseMoneyAll
} from '@/utils/helpers'
export default {
  mixins: [exchangeRate, country],
  name: 'MakePayment',
  props: {
    useType: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      showWireTransferInstructionDialog: false,
      transferType: 'terminal',
      breakpoints,
      showTransferProcessingDialog: false,
      showSuccessDialog: false
    }
  },
  methods: {
    showWireTransferInstruction () {
      this.showWireTransferInstructionDialog = true
    },
    parseMoneyNaira (val) {
      return ParseMoneyNaira(val)
    },
    parseMoney (val) {
      return ParseMoney(val)
    },
    parseMoneyAll (val, currency) {
      return ParseMoneyAll(val, currency)
    },
    onCopy: function (e) {
      this.$notify.success({
        title: 'Copied'
      })
    },
    onError: function (e) {
      this.$notify.error('Error copying to clipboard')
    },
    convertValueTo2DP (val) {
      return ConvertValueToXDP(val, 2)
    },
    closeTransferProcessingDialog () {
      this.showTransferProcessingDialog = false
    },
    showSuccess () {
      this.showSuccessDialog = true
    },
    closeSuccessDialog () {
      this.$router.push({ name: 'transactions' })
    },
    handleClose () {
      this.showWireTransferInstructionDialog = false
    },
    done () {
      if (this.routeName === 'terminal' && !this.usedBeneficiary) {
        this.showTransferProcessingDialog = true
      } else {
        this.$router.push('transactions')
      }
    }
  },
  components: {
    BankIconBig: () => import('@/assets/icons/BankIconBig.vue'),
    PlaidIcon: () => import('@/assets/icons/PlaidIcon.vue'),
    wireTransferDetails: () =>
      import('@/components/deposit/WireTransferDetails.vue'),
    ExternalApiPaymentTypeToggler: () =>
      import('@/components/externalApi/ExternalApiPaymentTypeToggler.vue'),
    TransactionProcessing: () => import('../TransactionProcessing.vue'),
    BeneficiarySavedSuccessDialog: () =>
      import('../BeneficiarySavedSuccessDialog.vue')
  },

  computed: {
    routeName () {
      return this.$route.name
    },
    usedBeneficiary () {
      return this.$store.state.terminal.usedBeneficiary
    },
    modalWidth () {
      return breakpoints.lgAndUp ? '35%' : breakpoints.mdAndUp ? '70%' : '91%'
    },
    recipientInfo () {
      return (
        this.$store.state.terminal.transferTerminalDetails
          .recipientInformation || {}
      )
    },
    transferDetails () {
      return (
        this.$store.state.terminal.transferTerminalDetails.transferDetails || {}
      )
    },
    transferReference () {
      const details = this.$store.state.terminal.transferInfo.localDepositRef
      return details
    },
    fromBalance () {
      return this.$store.state.terminal.terminalTransferType === 'fromBalance'
    },
    isExternalApi () {
      return this.useType === 'externalApi'
    }
  },
  destroyed () {
    this.$store.dispatch('terminal/resetTerminalState')
    this.$store.dispatch('terminal/setTransferDetails', {})
  }
}
</script>

<style scoped>
.box {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 2px 4px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
  height: 200px;
}
.card {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.1),
    0px 4px 8px rgba(3, 2, 7, 0.1);
  border-radius: 15px;
}
</style>
